/** @jsxImportSource @emotion/react */
import React, { memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { ark } from '@ark-ui/react';
import { useTheme } from '../provider/useTheme';
import type { ButtonSizeToken, ButtonTypeToken, SemanticColorToken, SpacingToken } from '../themes/types';
import type { IconName } from '../icons';
import { useTranslation } from '@vitvio/locales';
import CoreIcon from './CoreIcon';
import type { FontToken } from '../themes';
import { focusRing, transition } from '../constants/style';

const ArkButton = ark.button;

/**
 * Width presets used by CoreButton.
 */
export type ButtonWidthToken = 'auto' | 'sm' | 'md' | 'lg' | 'full';

/**
 * Shared CoreButton props.
 */
interface CoreButtonBaseProps {
  /** Translation key rendered as button text when `iconOnly` is not true. */
  label?: string;
  /** Optional leading icon; if `iconOnly` is true this icon is used as the glyph. */
  iconLeft?: IconName;
  /** Optional trailing icon; ignored when `iconLeft` is used with `iconOnly`. */
  iconRight?: IconName;
  /** Visual style variant. */
  type?: ButtonTypeToken;
  /** Size token controlling paddings and font. */
  size?: ButtonSizeToken;
  /** Width preset; `full` stretches to container width. */
  width?: ButtonWidthToken;
  /** Click callback. */
  onClick?: () => void;
}

type IconOnlyButtonProps = CoreButtonBaseProps & {
  /** Icon-only mode (requires `ariaLabelPath`). */
  iconOnly: true;
  /** Translation key used for the accessible button name. */
  ariaLabelPath: string;
};

type DefaultButtonProps = CoreButtonBaseProps & {
  /** Regular button mode (label and optional icons). */
  iconOnly?: false;
  /** Optional translation key for the accessible button name. */
  ariaLabelPath?: string;
};

/**
 * Props for the CoreButton component.
 */
export type ButtonProps = IconOnlyButtonProps | DefaultButtonProps;

const buttonDefaultColorTokens: Record<ButtonTypeToken, SemanticColorToken> = {
  primary: 'button.bg.primary',
  'primary.subtle': 'button.bg.primary.subtle',
  secondary: 'button.bg.secondary',
  ghost: 'button.bg.ghost',
  critical: 'button.bg.critical',
  'critical.subtle': 'button.bg.critical.subtle',
  'warning.subtle': 'button.bg.warning.subtle',
  ai: 'button.bg.ai',
};

const buttonActiveColorTokens: Record<ButtonTypeToken, SemanticColorToken> = {
  primary: 'button.bg.primary.active',
  'primary.subtle': 'button.bg.primary.subtle.active',
  secondary: 'button.bg.secondary.active',
  ghost: 'button.bg.ghost.active',
  critical: 'button.bg.critical.active',
  'critical.subtle': 'button.bg.critical.subtle.active',
  'warning.subtle': 'button.bg.warning.subtle.active',
  ai: 'button.bg.ai.active',
};

const buttonHoverColorTokens: Record<ButtonTypeToken, SemanticColorToken> = {
  primary: 'button.bg.primary.hover',
  'primary.subtle': 'button.bg.primary.subtle.hover',
  secondary: 'button.bg.secondary.hover',
  ghost: 'button.bg.ghost.hover',
  critical: 'button.bg.critical.hover',
  'critical.subtle': 'button.bg.critical.subtle.hover',
  'warning.subtle': 'button.bg.warning.subtle.hover',
  ai: 'button.bg.ai.hover',
};

const labelColorTokens: Record<ButtonTypeToken, SemanticColorToken> = {
  primary: 'fg.inverted.constant',
  'primary.subtle': 'fg',
  secondary: 'fg',
  ghost: 'fg',
  critical: 'fg.inverted.constant',
  'critical.subtle': 'fg',
  'warning.subtle': 'fg',
  ai: 'fg.inverted.constant',
};

const buttonPaddingHorizontalTokens: Record<ButtonSizeToken, SpacingToken> = {
  lg: '6',
  md: '4',
  sm: '3',
};

const buttonPaddingVerticalTokens: Record<ButtonSizeToken, SpacingToken> = {
  lg: '2.5',
  md: '2',
  sm: '1.5',
};

const iconOnlyButtonPaddingTokens: Record<ButtonSizeToken, SpacingToken> = {
  lg: '4',
  md: '3',
  sm: '2',
};

const fontTokens: Record<ButtonSizeToken, FontToken> = {
  lg: 'L_SemiBold',
  md: 'M_SemiBold',
  sm: 'S_SemiBold',
};

const buttonWidthTokens: Record<Exclude<ButtonWidthToken, 'auto' | 'full'>, SpacingToken> = {
  sm: '6',
  md: '10',
  lg: '16',
};

/**
 * Theme-aware button primitive used across design-system surfaces.
 */
export const CoreButton: React.FC<ButtonProps> = memo(
  ({
    iconLeft,
    iconRight,
    type = 'ghost',
    size = 'lg',
    width = 'auto',
    label,
    onClick,
    iconOnly = false,
    ariaLabelPath,
  }) => {
    const { getColor, getRadius, getFont, getSpacing } = useTheme();
    const { t } = useTranslation();

    const sizes = useMemo(() => {
      const hasWidthPadding = width !== 'auto' && width !== 'full';
      return {
        paddingHorizontal: hasWidthPadding
          ? getSpacing(buttonWidthTokens[width])
          : getSpacing(buttonPaddingHorizontalTokens[size]),
        paddingVertical: getSpacing(buttonPaddingVerticalTokens[size]),
        iconOnlyPadding: getSpacing(iconOnlyButtonPaddingTokens[size]),
        gap: getSpacing('2'),
        radius: getRadius('sm'),
        width: width === 'full' ? '100%' : 'auto',
        maxWidth: width === 'full' ? 'none' : getSpacing('80'),
      };
    }, [getRadius, getSpacing, size, width]);

    const colors = useMemo(() => {
      return {
        default: getColor(buttonDefaultColorTokens[type]),
        active: getColor(buttonActiveColorTokens[type]),
        hover: getColor(buttonHoverColorTokens[type]),
        label: getColor(labelColorTokens[type]),
        labelToken: labelColorTokens[type],
      };
    }, [getColor, type]);

    const fontProperties = useMemo(() => getFont(fontTokens[size]), [getFont, size]);

    return (
      <ArkButton
        type="button"
        onClick={onClick}
        aria-label={ariaLabelPath ? t(ariaLabelPath) : undefined}
        css={css`
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: ${colors.label};
          font-family: ${fontProperties.fontFamily};
          font-size: ${fontProperties.size}px;
          font-weight: ${fontProperties.weight};
          line-height: ${fontProperties.lineHeight}px;
          letter-spacing: ${fontProperties.letterSpacing};
          background-color: ${colors.default};
          &:hover {
            background-color: ${colors.hover};
          }
          &:active {
            background-color: ${colors.active};
          }
          &:focus-visible {
            outline: ${focusRing.width} solid ${getColor('border.accent')};
            outline-offset: ${focusRing.offset};
          }
          border: none;
          border-radius: ${sizes.radius};
          padding: ${iconOnly ? sizes.iconOnlyPadding : sizes.paddingVertical}
            ${iconOnly ? sizes.iconOnlyPadding : sizes.paddingHorizontal};
          gap: ${sizes.gap};
          transition: background-color ${transition.fast};
          width: ${sizes.width};
          max-width: ${sizes.maxWidth};
          overflow: ${sizes.width === '100%' ? 'hidden' : 'visible'};
          text-overflow: ${sizes.width === '100%' ? 'ellipsis' : 'clip'};
          white-space: ${sizes.width === '100%' ? 'nowrap' : 'normal'};
          cursor: pointer;
        `}
      >
        {iconOnly ? (
          <CoreIcon icon={iconLeft ?? iconRight ?? null} size="md" color={colors.labelToken} />
        ) : (
          <>
            {iconLeft && <CoreIcon icon={iconLeft} size="md" color={colors.labelToken} />}
            {label && t(label)}
            {iconRight && <CoreIcon icon={iconRight} size="md" color={colors.labelToken} />}
          </>
        )}
      </ArkButton>
    );
  },
);

CoreButton.displayName = 'CoreButton';

export default CoreButton;
